/**
 * Which decoded audio a same-project reload is allowed to reuse.
 *
 * A board repush reloads the whole project (setSqlitePath{refresh:true}) and the load gates
 * `projectLoaded` on audio being ready — so dropping every decoded AudioBuffer and re-decoding
 * the project's entire audio is a cost every add-shot pays, and it grows with the project.
 * Nothing about an add-shot can change a clip's bytes, but a delete genuinely can (HideScene
 * cleans audio rows, cs:5443), so the reload compares what it decoded last time against what the
 * incoming DB carries and re-decodes only the difference.
 *
 * The fingerprint is deliberately about the DECODED ARTIFACT, not the clip's placement: that
 * artifact is a pure function of the clip's raw bytes (decodeAudio takes bytes and nothing else;
 * peaks derive from the buffer), so shotId / shotOffset / duration / bufferOffset / volume /
 * fades / lane / isMuted / label / color — every field consumed at PLAY time — are absent by
 * design. A field belongs here only if a change to it could mean different bytes.
 */
import type { AudioClip } from '@/types/timeline.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';

export interface AudioClipFingerprint {
  id: number;
  /** decodeAudio doesn't read it (the decoder sniffs the container), but a changed mime means the
   *  row's bytes were replaced — free evidence, so it counts. */
  mimeType: string;
  /** file:// source the load path fetches from; null on rows Unity stores as a BLOB. */
  audioFileUrl: string | null;
  /** CDN source for TTS clips (Unity downloads these natively). */
  cdnUrl: string | null;
  /** Length of the clip's stashed raw bytes, or null when it has none yet — a URL-sourced clip
   *  before its fetch, or a row whose audiodata is NULL. Null never matches anything: an
   *  unproven clip re-decodes rather than risk serving a buffer decoded from other bytes. */
  byteLength: number | null;
}

export interface AudioReconcilePlan {
  /** Clips whose cached buffer + peaks stay untouched — no fetch, no decode, no revoke. */
  toKeep: number[];
  /** Clips that must be fetched (URL rows) and decoded: new to the project, or replaced. */
  toLoad: number[];
  /** Clips whose cached artifact must be freed: gone from the project, or replaced. */
  toRelease: number[];
}

/**
 * Fingerprint the clips as they stand right now. Byte lengths are read from the blob store, so
 * this is only meaningful once the loader has stashed the incoming bytes (previsLoader does that
 * before loadProject runs) — a clip whose bytes arrive later fingerprints as unproven and reloads.
 */
export function fingerprintAudioClips(clips: AudioClip[]): AudioClipFingerprint[] {
  return clips.map(c => ({
    id: c.id,
    mimeType: c.mimeType,
    audioFileUrl: c.audioFileUrl ?? null,
    cdnUrl: c.cdnUrl ?? null,
    byteLength: getBlob(c.id)?.byteLength ?? null,
  }));
}

function isSameSource(a: AudioClipFingerprint, b: AudioClipFingerprint): boolean {
  return a.byteLength !== null
    && a.byteLength === b.byteLength
    && a.mimeType === b.mimeType
    && a.audioFileUrl === b.audioFileUrl
    && a.cdnUrl === b.cdnUrl;
}

/**
 * Split the incoming clips against the previously loaded ones. A replaced clip lands in BOTH
 * toLoad and toRelease — the stale artifact has to be freed before the fresh bytes are decoded
 * under the same id, or the decode cache would just hand the old buffer back.
 */
export function reconcileAudioClips(
  previous: AudioClipFingerprint[],
  incoming: AudioClipFingerprint[],
): AudioReconcilePlan {
  const previousById = new Map(previous.map(fp => [fp.id, fp]));
  const plan: AudioReconcilePlan = { toKeep: [], toLoad: [], toRelease: [] };

  const incomingIds = new Set<number>();
  for (const fp of incoming) {
    incomingIds.add(fp.id);
    const before = previousById.get(fp.id);
    if (before && isSameSource(before, fp)) {
      plan.toKeep.push(fp.id);
      continue;
    }
    // Only a clip we actually loaded before can hold a stale artifact. One added in-session
    // (TTS, recording, import) was never fingerprinted, so it has nothing to free — and its
    // buffer is already correct, which is why the decode below costs it nothing.
    if (before) plan.toRelease.push(fp.id);
    plan.toLoad.push(fp.id);
  }

  for (const fp of previous) {
    if (!incomingIds.has(fp.id)) plan.toRelease.push(fp.id);
  }

  return plan;
}

// ── Last-load snapshot ──────────────────────────────────────────────────
// The old side of the reconcile. Recorded at the end of every load (fresh ones included, so the
// next refresh has something to compare against) and read only by a same-project refresh.

let lastLoaded: AudioClipFingerprint[] = [];

export function recordLoadedAudio(fingerprints: AudioClipFingerprint[]): void {
  lastLoaded = fingerprints;
}

export function loadedAudioFingerprints(): AudioClipFingerprint[] {
  return lastLoaded;
}
